import { NavIcon } from '@/components/icons/NavIcon';
import { Card } from '@/components/ui/card';
import { getGuideBonusesData, type GuideBonusesData } from '@/lib/bonuses-client';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

type AuraPolicy = NonNullable<GuideBonusesData['bonusesPayload']['auraPolicies']>[number];

type AuraReferenceEntry = {
  id: number;
  name: string;
  sourceRaces: string[];
  publishedDescription: string;
  effects: string[];
};

function formatPercent(raw: number): string {
  const percent = Math.round(raw * 1000) / 10;
  return `${percent > 0 ? '+' : ''}${percent}%`;
}

function numberList(value: unknown): number[] {
  if (Array.isArray(value)) {
    return value.flatMap(entry => numberList(entry));
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(entry => Number(entry.trim()))
      .filter(Number.isFinite)
      .map(entry => Math.trunc(entry));
  }

  const numeric = Number(value);
  return Number.isFinite(numeric) ? [Math.trunc(numeric)] : [];
}

function bonusLabel(data: GuideBonusesData, bonusId: number): string {
  const raw = `${data.stringsPayload?.bonstr?.[`${bonusId}`]?.[0] || `Effect ${bonusId}`}`;
  return raw
    .replaceAll('!', '')
    .replaceAll('$', '')
    .replace(/\|[^|]+\|/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function auraStackBonusValue(data: GuideBonusesData, auraId: number, bonusId: number): number {
  const aura = data.bonusesPayload.ageb?.[`${auraId}`] || {};
  const direct = Number(aura[`${bonusId}`]) || 0;
  if (direct !== 0) {
    return direct;
  }
  if (auraId === 337 && bonusId === 383) {
    return Number(aura['337']) || 0;
  }
  if (auraId === 337 && bonusId === 337) {
    return Number(aura['383']) || 0;
  }
  return direct;
}

function auraPolicyText(data: GuideBonusesData, auraId: number, policy: AuraPolicy): string {
  const label = policy.bonusIndex === 383 ? 'Incoming Attack Time' : bonusLabel(data, policy.bonusIndex);
  const stackValue = auraStackBonusValue(data, auraId, policy.bonusIndex);
  const capText = policy.capValue === null || policy.capValue === undefined ? '' : ` (cap ${formatPercent(policy.capValue)})`;
  const targetText =
    policy.targetMode === 'enemy_kingdom'
      ? ' Applies to the enemy kingdom in war.'
      : policy.targetMode === 'both_kingdoms'
        ? ' Applies to both kingdoms in war.'
        : ' Applies to your own kingdom in war.';
  const requirementText = policy.requiresEnemyTarget ? ' Requires an active enemy war target.' : '';

  return `${label}: ${formatPercent(stackValue)} per matching race${capText}.${targetText}${requirementText}`;
}

function auraEntries(data: GuideBonusesData): AuraReferenceEntry[] {
  const auraIds = new Set<number>();

  for (const policy of data.bonusesPayload.auraPolicies || []) {
    auraIds.add(Number(policy.auraIndex));
  }

  for (const race of data.races) {
    const auraValues = data.bonusesPayload.ageb?.[`${race.id}`]?.['321'];
    for (const auraId of numberList(auraValues)) {
      auraIds.add(auraId);
    }
  }

  return [...auraIds]
    .filter(Number.isFinite)
    .sort((a, b) => a - b)
    .map(auraId => {
      const sourceRaces = data.races
        .filter(race => numberList(data.bonusesPayload.ageb?.[`${race.id}`]?.['321']).includes(auraId))
        .map(race => race.name);

      const effects = (data.bonusesPayload.auraPolicies || [])
        .filter(policy => Number(policy.auraIndex) === auraId)
        .sort((a, b) => Number(a.bonusIndex) - Number(b.bonusIndex))
        .map(policy => auraPolicyText(data, auraId, policy));

      return {
        id: auraId,
        name: bonusLabel(data, auraId) || `Aura #${auraId}`,
        sourceRaces,
        publishedDescription: `${data.stringsPayload?.bonstr?.[`${auraId}`]?.[1] || ''}`.trim(),
        effects,
      };
    });
}

export const Route = createFileRoute('/classic-guide/auras')({
  component: ClassicGuideAurasPage,
});

function ClassicGuideAurasPage() {
  const bonusesQuery = useQuery({
    queryKey: ['guide-bonuses', 'auras'],
    queryFn: getGuideBonusesData,
    staleTime: 1000 * 60 * 10,
  });

  const entries = bonusesQuery.data ? auraEntries(bonusesQuery.data) : [];

  return (
    <div className="space-y-5">
      <Card className="p-6">
        <div className="flex items-center gap-4">
          <NavIcon
            code="races"
            width={72}
            height={72}
            className="shrink-0 overflow-hidden rounded-xl border border-[#d4af37]/25 shadow-[0_0_18px_rgba(212,175,55,0.16)]"
          />
          <div className="min-w-0">
            <h2 className="font-cinzel text-3xl text-[#f0f0f0]">Aura Reference</h2>
            <p className="mt-1 text-base leading-7 text-[#b8b8b8]">
              Kingdom auras and what they add in war.
            </p>
          </div>
        </div>
      </Card>

      <Card className="space-y-5 p-6">
        <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
          Auras are kingdom-wide race effects. They matter most in war, and they grow stronger as more provinces
          in the kingdom match the same race. The list below is taken from the current age bonus data.
        </p>

        {bonusesQuery.isLoading ? <p className="text-sm text-[#b8b8b8]">Loading current age aura data...</p> : null}
        {bonusesQuery.isError ? (
          <p className="text-sm text-[#b8b8b8]">
            Current age aura data could not be loaded. The guide app needs the published bonuses and strings bundle
            to render this page.
          </p>
        ) : null}

        {bonusesQuery.data && entries.length ? (
          <div className="space-y-4">
            {entries.map(entry => (
              <Card key={entry.id} className="space-y-4 p-5">
                <div className="space-y-2">
                  <h3 className="font-cinzel text-2xl text-[#e6c547]">{entry.name}</h3>
                  {entry.sourceRaces.length ? (
                    <p className="text-[1.0625rem] leading-7 text-[#e3e3e3]">
                      <span className="text-[#ffe58a]">Race:</span> {entry.sourceRaces.join(', ')}
                    </p>
                  ) : null}
                  {entry.publishedDescription ? (
                    <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">{entry.publishedDescription}</p>
                  ) : null}
                </div>

                {entry.effects.length ? (
                  <ul className="space-y-2 rounded-lg border border-[#d4af37]/15 bg-[#050505]/50 px-5 py-4 text-[1.0625rem] leading-7 text-[#e8dfcf]">
                    {entry.effects.map(effect => (
                      <li key={effect} className="flex gap-3">
                        <span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-[#e6c547]" />
                        <span>{effect}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-[1.0625rem] leading-8 text-[#e3e3e3]">
                    No separate stacking rule is published for this aura in the current age bundle.
                  </p>
                )}
              </Card>
            ))}
          </div>
        ) : null}

        {bonusesQuery.data && !entries.length && !bonusesQuery.isLoading ? (
          <p className="text-sm text-[#b8b8b8]">No aura entries were found in the current age bundle.</p>
        ) : null}
      </Card>
    </div>
  );
}
